/**
 * Calendrier FullCalendar v6 (§ 11.2) : vues mois / semaine / liste, avec
 * bascule calendrier / liste mémorisée par utilisateur, et codes visuels
 * par statut (planifiée, annulée barrée, vacances, ponctuelle, activité).
 */
import React, { useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { api } from '../lib/api.js';
import { fmtDate } from '../lib/format.js';
import { Badge } from './common.jsx';

/** Applique les codes visuels du § 11.2 à un événement. */
export function decorate(event) {
  const p = event.extendedProps || {};
  const out = { ...event };
  if (p.type === 'activite') {
    out.color = '#1d7a5f';
  } else if (p.statut === 'annulee') {
    out.color = '#a0a0a0';
    out.className = 'adlt-event-annule';
  } else if (p.statut === 'vacances') {
    out.color = '#8a8496';
  } else if (p.statut === 'deplacee') {
    out.color = '#b98900';
    out.className = 'adlt-event-annule';
  } else if (p.ponctuelle) {
    out.color = '#59308d';
    out.title = '★ ' + out.title;
  } else {
    out.color = '#4a63b8';
  }
  return out;
}

export function CalendarView({ fetchEvents, onEventClick, initialMode }) {
  const [mode, setMode] = useState(initialMode || localStorage.getItem('adlt_vue') || 'calendrier');
  const [listEvents, setListEvents] = useState(null);

  function switchMode(m) {
    setMode(m);
    localStorage.setItem('adlt_vue', m);
    api('me/preferences', { method: 'PATCH', body: { vue_calendrier: m } });
    if (m === 'liste') loadList();
  }

  async function loadList() {
    const du = new Date();
    const au = new Date(Date.now() + 60 * 86400000);
    const events = await fetchEvents(du.toISOString().slice(0, 10), au.toISOString().slice(0, 10));
    setListEvents(events);
  }

  const sources = useMemo(() => (info, success, failure) => {
    fetchEvents(info.startStr.slice(0, 10), info.endStr.slice(0, 10))
      .then((events) => success(events.map(decorate)))
      .catch(failure);
  }, [fetchEvents]);

  React.useEffect(() => { if (mode === 'liste') loadList(); }, []); // eslint-disable-line

  return (
    <div>
      <div className="btn-group mb-3" role="group" aria-label="Vue">
        <button type="button" className={`btn btn-sm ${mode === 'calendrier' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => switchMode('calendrier')}>Calendrier</button>
        <button type="button" className={`btn btn-sm ${mode === 'liste' ? 'btn-primary' : 'btn-outline-primary'}`} onClick={() => switchMode('liste')}>Liste</button>
      </div>
      {mode === 'calendrier' ? (
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{ left: 'prev,next today', center: 'title', right: 'dayGridMonth,timeGridWeek,listWeek' }}
          locale={frLocale}
          height="auto"
          events={sources}
          eventClick={(info) => onEventClick && onEventClick({ id: info.event.id, title: info.event.title, start: info.event.startStr, ...info.event.extendedProps })}
        />
      ) : (
        <div className="list-group">
          {listEvents === null && <div className="text-muted p-3">Chargement…</div>}
          {listEvents !== null && listEvents.length === 0 && <div className="text-muted p-3">Rien à afficher sur les 60 prochains jours.</div>}
          {(listEvents || []).map((e) => (
            <button type="button" key={e.id} className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
              onClick={() => onEventClick && onEventClick({ id: e.id, title: e.title, start: e.start, ...e.extendedProps })}>
              <span className="d-flex align-items-center gap-2">
                {e.extendedProps?.image && <img src={e.extendedProps.image} alt="" width="44" height="44" className="rounded flex-shrink-0" style={{ objectFit: 'cover' }} />}
                <span>
                  <strong className={['annulee', 'deplacee'].includes(e.extendedProps?.statut) ? 'text-decoration-line-through text-muted' : ''}>{e.title}</strong>
                  <br /><small className="text-muted">{fmtDate(e.start)} · {String(e.start).slice(11, 16)}{e.extendedProps?.lieu ? ' · ' + e.extendedProps.lieu : ''}</small>
                </span>
              </span>
              {e.extendedProps?.statut && <Badge statut={e.extendedProps.statut} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
